import { List, ListItem, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { EnrollInAutoPayArgs, enrollInAutoPay } from '../../api/autoPay/enroll';
import { useBillsQuery } from '../../api/getBills';
import { StatusType } from '../../constants/enums';
import { AutoPayMessages } from '../../constants/paymentMessages';
import { useSnackbarsContext } from '../../store/SnackbarProvider';
import { usePaymentsContext } from '../../store/payments';

export const useAutoPayEnrollMutation = (
  isBulkEnroll = false,
  isEdit = false
) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: billsData } = useBillsQuery();
  const { enqueueSnackbar } = useSnackbarsContext();
  const { state } = usePaymentsContext();

  const sendSnackbar = (success: boolean, items: any) => {
    let content;

    if (success) {
      content = (
        <>
          <Typography
            sx={{
              fontFamily: 'Cabin',
              fontWeight: 400,
              fontSize: { mobile: 16, tablet: 18 },
            }}
          >
            {AutoPayMessages.BULK_SUCCESS}
          </Typography>
          <List sx={{ listStyleType: 'disc', px: { mobile: 0, tablet: 5 } }}>
            {items.map(({ policyNumber }: { policyNumber: string }) => (
              <ListItem
                key={policyNumber}
                sx={{
                  display: { tablet: 'list-item' },
                  py: 0,
                  px: { mobile: 0, tablet: 2 },
                  fontFamily: 'Cabin',
                  fontSize: { mobile: 16, tablet: 18 },
                  fontWeight: 400,
                }}
              >
                {billsData?.payments.find(
                  (payment) => payment.policyNumber === policyNumber
                )?.policyType ?? ''}{' '}
                policy {policyNumber}
              </ListItem>
            ))}
          </List>
        </>
      );
    } else {
      content =
        'We are unable to process your request right now. Please try again later.';
    }

    enqueueSnackbar({
      content,
      variant: success ? StatusType.SUCCESS : StatusType.ERROR,
      autoHideDuration: 10000,
    });
  };

  const enroll = useMutation({
    mutationFn: (args: EnrollInAutoPayArgs) => enrollInAutoPay(args),

    onSuccess: async ({ data }) => {
      if (isBulkEnroll) {
        const faults = data.filter((res: any) => res.fault);
        const successes = data.filter((res: any) => !res.fault);
        await queryClient.refetchQueries({
          queryKey: ['bills'],
        });

        successes.length > 0 && sendSnackbar(true, successes);
        faults.length > 0 && sendSnackbar(false, faults);
      } else {
        const foundFault = data.find((res: any) => res.fault);

        if (foundFault) {
          enqueueSnackbar({
            message: AutoPayMessages.FAIL,
            variant: StatusType.ERROR,
            autoHideDuration: 10000,
          });
        } else {
          await queryClient.refetchQueries({
            queryKey: ['bills'],
          });
          const message = `${
            isEdit ? AutoPayMessages.SUCCESS_EDIT : AutoPayMessages.SUCCESS_NEW
          } ${state.selectedBill.policyType.toLowerCase()} policy ${
            state.selectedBill.policyNumber
          }.`;
          enqueueSnackbar({
            message,
            variant: StatusType.SUCCESS,
            autoHideDuration: 10000,
          });
        }
      }
    },
    onError: (err: any) => {
      console.error(err);
      enqueueSnackbar({
        message: AutoPayMessages.FAIL,
        variant: StatusType.ERROR,
        autoHideDuration: 10000,
      });
    },
    onSettled: () => navigate('/autopay'),
  });

  return { enroll };
};
